import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  TextField,
  Text,
  BlockStack,
  Button,
  Badge,
  Spinner,
  Toast,
  Modal,
  Layout,
  Icon,
} from '@shopify/polaris';
import { authService } from '../services/authService';
import { LockMajor, CircleTickMajor } from '@shopify/polaris-icons';

const sectionStyle = { marginBottom: 32};
const cardSectionStyle = {
  paddingTop: 32,
  paddingBottom: 32,
  paddingLeft: 24,
  paddingRight: 24,
  marginTop: 24,
  marginBottom: 24,
};

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Password change form state
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Load user profile on component mount
  const loadUserProfile = useCallback(async () => {
    try {
      const userData = await authService.getProfile();
      setUser(userData);
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback to stored user data
      const storedUser = authService.getUser();
      if (storedUser) {
        setUser(storedUser);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const handlePasswordChange = useCallback((field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [passwordErrors]);

  const validatePasswordForm = useCallback(() => {
    const errors = {};
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  }, [passwordData]);

  const handleChangePassword = useCallback(async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setIsChangingPassword(true);
    try {
      await authService.changePassword(passwordData.newPassword);
      
      setToastMessage('Password changed successfully');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      
      // Reset form
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordErrors({});
      setIsChangePasswordModalOpen(false);
    } catch (error) {
      console.error('Error changing password:', error);
      setToastMessage(`Error: ${error.message || 'Failed to change password'}`);
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 5000);
    } finally {
      setIsChangingPassword(false);
    }
  }, [passwordData, validatePasswordForm]);

  const handleClosePasswordModal = useCallback(() => {
    setIsChangePasswordModalOpen(false);
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordErrors({});
  }, []);

  // Determine if user is super admin
  const isSuperAdmin = user?.group === 'Super Admin';

  if (isLoading) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '60px 16px', textAlign: 'center' }}>
              <BlockStack gap="400" align="center">
                <Spinner size="large" />
                <Text variant="bodyMd" tone="subdued">Loading profile...</Text>
              </BlockStack>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <Layout>


      <Layout.Section oneHalf>
        <div style={sectionStyle}>
          <Card sectioned style={cardSectionStyle}>
            <BlockStack gap="400" style={{ padding: '16px' }}>
              <div style={{ width: '100%' }}>
                <Text variant="bodyMd" fontWeight="semibold">Email</Text>
                <Text variant="bodyMd">{user?.email || 'Not available'}</Text>
              </div>
              <div style={{ width: '100%' }}>
                <Text variant="bodyMd" fontWeight="semibold">Name</Text>
                <Text variant="bodyMd">{user?.name || 'Not available'}</Text>
              </div>
              <div style={{ width: '100%' }}>
                <Text variant="bodyMd" fontWeight="semibold">User ID</Text>
                <Text variant="bodyMd">{user?.id || 'Not available'}</Text>
              </div>
              <div style={{ width: '100%' }}>
                <Text variant="bodyMd" fontWeight="semibold">Group</Text>
                <Text variant="bodyMd">{user?.group || 'No group assigned'}</Text>
              </div>
            </BlockStack>
          </Card>
        </div>
      </Layout.Section>

      <Layout.Section oneHalf>
        <div style={sectionStyle}>
          <Card sectioned style={cardSectionStyle}>
            <BlockStack gap="400" style={{ padding: '16px' }}>
              <div style={{ width: '100%' }}>
                <Text variant="bodyMd" fontWeight="semibold">Permissions</Text>
                {Array.isArray(user?.permissions) && user.permissions.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {user.permissions.map((_, index) => (
                        <li key={index} style={{ height: 32, display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                          <Icon source={CircleTickMajor} color="success" />
                        </li>
                      ))}
                    </ul>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {user.permissions.map((permission, index) => (
                        <li key={index} style={{ height: 32, display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                          <Text variant="bodyMd">{permission}</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <Text variant="bodyMd" tone="subdued">No permissions assigned</Text>
                )}
              </div>
            </BlockStack>
          </Card>
        </div>
      </Layout.Section>

      <Layout.Section>
        <div style={sectionStyle}>
          <Card sectioned style={cardSectionStyle}>
            <BlockStack gap="400" style={{ padding: '16px' }}>
              <div style={{ width: '100%' }}>
                <Text variant="bodyMd" fontWeight="semibold">Password</Text>
                <Text variant="bodyMd" tone="subdued">
                  Change your account password
                </Text>
                <div style={{ marginTop: '8px' }}>
                  <Button 
                    icon={LockMajor}
                    onClick={() => setIsChangePasswordModalOpen(true)}
                    variant="primary"
                  >
                    Change Password
                  </Button>
                </div>
              </div>
              {isSuperAdmin && (
                <div style={{ width: '100%' }}>
                  <Text variant="bodyMd" fontWeight="semibold">Admin Actions</Text>
                  <Text variant="bodyMd" tone="subdued">
                    Additional admin features available
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Badge tone="success">Super Admin Access</Badge>
                  </div>
                </div>
              )}
            </BlockStack>
          </Card>
        </div>
      </Layout.Section>

      {/* Change Password Modal */}
      <Modal
        open={isChangePasswordModalOpen}
        onClose={handleClosePasswordModal}
        title="Change Password"
        primaryAction={{
          content: 'Change Password',
          onAction: handleChangePassword,
          loading: isChangingPassword,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleClosePasswordModal,
          },
        ]}
      >
        <BlockStack gap="400">
          <TextField
            label="New Password"
            type="password"
            value={passwordData.newPassword}
            onChange={(value) => handlePasswordChange('newPassword', value)}
            error={passwordErrors.newPassword}
            autoComplete="new-password"
            helpText="Password must be at least 8 characters long"
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(value) => handlePasswordChange('confirmPassword', value)}
            error={passwordErrors.confirmPassword}
            autoComplete="new-password"
          />
        </BlockStack>
      </Modal>

      {/* Toast for notifications */}
      {toastActive && (
        <Toast
          content={toastMessage}
          tone={toastMessage.includes('Error') ? 'critical' : 'success'}
          onDismiss={() => setToastActive(false)}
          action={{
            content: 'Dismiss',
            onAction: () => setToastActive(false),
          }}
        />
      )}
    </Layout>
  );
};

export default ProfilePage; 